import { createSlice } from '@reduxjs/toolkit';
import { incrementMetric } from '../../../Services/Datadog/Datadog';

export const legalStatement = createSlice({
  name: 'LEGAL_STATEMENT',
  initialState: {}, // this comes from preloadedState in the store

  reducers: {
    setLegalStatement(state, action) {
      return {
        ...state,
        ...action.payload,
        supplierLegalStatements: {
          ...state.supplierLegalStatements,
          ...action.payload.supplierLegalStatements,
        },
      };
    },
    updateIsLegalStatementMounted(state, action) {
      return {
        ...state,
        isLegalStatementMounted: action.payload?.isLegalStatementMounted,
      };
    },
    updateIsSuppliersListModalOpen(state, action) {
      return {
        ...state,
        isSuppliersListModalOpen: action.payload?.isSuppliersListModalOpen,
      };
    },
    /**
     * When dynamic height is enabled, the legal consent statement needs to be visible prior to navigating to the penultimate page.
     * Invoking setHandleSideEffectsOfLegalStatement from within the useNavigation hook followed by
     * updateShouldRenderLegalStatement from within <DynamicWidgetHeight/> takes care of that requirement.
     */
    setHandleSideEffectsOfLegalStatement(state, action) {
      return {
        ...state,
        handleSideEffectsOfLegalStatement:
          action.payload?.handleSideEffectsOfLegalStatement,
      };
    },
    setSideEffectDetailsForLegalStatement(state, action) {
      return {
        ...state,
        sideEffectDetailsForLegalStatement: {
          destinationPageNumber:
            action.payload?.sideEffectDetailsForLegalStatement
              .destinationPageNumber,
          navigationDirection:
            action.payload?.sideEffectDetailsForLegalStatement
              .navigationDirection,
        },
      };
    },
    updateShouldRenderLegalStatement(state, action) {
      return {
        ...state,
        shouldRenderLegalStatement: action.payload?.shouldRenderLegalStatement,
      };
    },
    updateIsLegalStatementRevealed(state, action) {
      return {
        ...state,
        isLegalStatementRevealed: action.payload?.isLegalStatementRevealed,
      };
    },
    updateHasStatementGraduallyDisappeared(state, action) {
      return {
        ...state,
        hasStatementGraduallyDisappeared:
          action.payload?.hasStatementGraduallyDisappeared,
      };
    },

    getRequestFailed(state, action) {
      /**
       * If we have failed to get a LEGAL_STATEMENT we want to inform Datadog
       */
      incrementMetric({
        name: 'getLegalStatementFailed',
        tags: {
          error_thrown: 'Failed to get a Legal Statement from Eagle', // DO not change: this is used by Datadog
          locale_code: action.payload?.localeCode,
          subcategory_id: action.payload?.subcategoryId,
          include_supplier_legal_statements: action.payload
            ?.includeSupplierLegalStatements
            ? 'true'
            : 'false',
          error: action.payload?.error,
        },
      });

      /**
       * And ensure that the legalStatement is removed from state so that we do not submit a lead from a user who has not legally consented
       */
      state.statement = null;
    },

    setStatementOverride(state, action) {
      return {
        ...state,
        statementOverride: action.payload?.statementOverride,
      };
    },
  },
});

const { actions, reducer } = legalStatement;

export const {
  setLegalStatement,
  getRequestFailed,
  updateIsLegalStatementMounted,
  updateIsSuppliersListModalOpen,
  setHandleSideEffectsOfLegalStatement,
  updateShouldRenderLegalStatement,
  updateIsLegalStatementRevealed,
  updateHasStatementGraduallyDisappeared,
  setSideEffectDetailsForLegalStatement,
  setStatementOverride,
} = actions;

export default reducer;
